/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {TableHeaderEntryModel} from '../../../shared/table/models/table-header-entry.model';
import {ReferenceDataModel} from '../../../api/reference-data.model';
import {TableLineDataModel} from '../../../shared/table/models/table-line-data.model';
import {CurrencyBaseModel} from '../../models/currency/currency-base.model';
import {CriterionBaseModel} from '../../models/criterion-base/criterion-base.model';
import {ValueBaseModel} from '../../models/value-base/value-base.model';
import {BundleDetailModel} from '../../models/bundle/bundle-detail/bundle-detail.model';
import {BundlePositionsDetailModel} from '../../models/bundle/bundle-positions-detail/bundle-positions-detail.model';

type DataValue = CriterionBaseModel | ValueBaseModel;
type ListType = BundleDetailModel | BundlePositionsDetailModel;

@Injectable({
    providedIn: 'root'
})
export class VariableColumnsService {

    /**
     * Populated a table header with the entries for variable columns
     * @param originalMap the header entries original map
     * @param specs an array containing the configuration for the new table entries
     * @param codesArray an to store the new header entries codes
     * @param headerBaseConfiguration an base configuration to be passed as the new header entry configuration (display)
     * @param insertIndex the index into which the new entries is to be be inserted
     * @param keySuffix a suffix to be added to the entry key to ensure unique keys
     */
    addVariableColumnsHeaders(originalMap: Map<string, TableHeaderEntryModel<string>>,
                              specs: ReferenceDataModel[],
                              codesArray: string[],
                              headerBaseConfiguration: TableHeaderEntryModel<string>,
                              insertIndex: number,
                              keySuffix: string): void {
        if (specs?.length > 0 && codesArray != null) {

            specs.forEach((spec) => {
                const key = keySuffix != null ? `${spec.code}_${keySuffix}` : spec.code;
                const value = {...headerBaseConfiguration, code: spec.code, name: spec.name};

                codesArray.push(spec.code);

                this.insertAtMapIndex(insertIndex, key, value, originalMap);
            });
        }
    }

    /**
     * Populates a table line with the values for variable columns
     * @param originalMap the line values original map
     * @param codesArray an array containing the codes corresponding to the variable columns headers
     * @param insertIndex the index into which the new values is to be be inserted
     * @param dataValues an array containing the values to be inserted
     * @param keySuffix a suffix to be added to the entry key to ensure unique keys
     * @param currency optional parameter to be passed in case the value is financial
     */
    addVariableColumnsValues(originalMap: Map<string, TableLineDataModel>,
                             codesArray: string[],
                             insertIndex: number,
                             dataValues: DataValue[],
                             keySuffix: string,
                             currency?: CurrencyBaseModel): void {
        if (codesArray?.length > 0 && dataValues != null) {

            codesArray.forEach((specCode) => {
                const key = keySuffix != null ? `${specCode}_${keySuffix}` : specCode;
                const cellValue = dataValues.find((entry) => entry.specCode === specCode)?.value;

                let value;
                if (currency != null) {
                    value = {
                        cellValue: {
                            data: cellValue,
                            currency
                        },
                        copyPasteValue: cellValue
                    };
                } else {
                    value = {cellValue};
                }
                this.insertAtMapIndex(insertIndex, key, value, originalMap);
            });
        }
    }

    /**
     * Returns all the additional criteria specs existing on a given list list
     */
    getCriteriaSpecs(tableList: ListType[]): ReferenceDataModel[] {
        const criteriaSpecs: ReferenceDataModel[] = [];
        tableList?.forEach((entry) => {
            entry.spec?.criteriaSpecs?.forEach((spec) => {
                if (criteriaSpecs.find((item) => item.code === spec.code) == null) {
                    criteriaSpecs.push(spec);
                }
            });
        });
        // Reverse the array to ensure the BE order is maintained
        return criteriaSpecs.reverse();
    }

    /**
     * Returns all the additional value specs existing on a given list
     */
    getValuesSpecs(tableList: ListType[]): ReferenceDataModel[] {
        const valuesSpecs: ReferenceDataModel[] = [];
        tableList?.forEach((entry) => {
            entry.spec?.valuesSpecs?.forEach((spec) => {
                if (valuesSpecs.find((item) => item.code === spec.code) == null) {
                    valuesSpecs.push(spec);
                }
            });
        });
        // Reverse the array to ensure the BE order is maintained
        return valuesSpecs.reverse();
    }


    /**
     * Inserts a key-value into to a map in a specific index
     * @param index the position index into which the key-value is to be inserted
     * @param key the key to be inserted
     * @param value the value to be inserted
     * @param map the map into which the key value is to be inserted
     */
    private insertAtMapIndex<T>(index: number, key: string, value: T, map: Map<string, T>): void {
        const arr = Array.from(map);
        arr.splice(index, 0, [key, value]);
        map.clear();
        arr.forEach(([k, v]) => map.set(k, v));
    }
}
